import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../api/auth";

const SettingsContext = createContext(null);

const DEFAULTS = {
  logoUrl: null,
  primaryColor: "#006B3C",
  secondaryColor: "#B8933A",
  heroTitle: "Voyagez à travers le Gabon avec SETRAG",
  heroSubtitle: "Réservez votre billet de train en ligne, simplement et rapidement.",
  privacyPolicy: "",
  termsOfService: "",
  tariffs: [],
  bannerText: "",
  bannerActive: false,
};

/**
 * Charge la configuration du site (GET /settings, public) une seule fois et
 * la rend disponible partout via useSettings(). Applique aussi les couleurs
 * de marque comme variables CSS globales (--brand-primary/--brand-secondary)
 * pour que les nouveaux composants (bande d'annonce, pages légales, etc.)
 * suivent la personnalisation faite par l'administrateur.
 *
 * Remarque : la majorité des pages existantes (Home, Dashboard, AdminDashboard…)
 * utilisent des couleurs codées en dur dans leurs styles inline plutôt que ces
 * variables CSS - les faire suivre la couleur choisie par l'administrateur
 * demanderait de remplacer ces constantes par `var(--brand-primary)` dans
 * chaque fichier. Ce contexte pose la base (source de vérité + variables
 * globales déjà appliquées) mais cette migration reste à terminer page par page.
 */
export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API.MDP}/settings`);
      setSettings({ ...DEFAULTS, ...res.data });
    } catch {
      // Hors-ligne ou serveur injoignable : on garde les valeurs par défaut.
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--brand-primary", settings.primaryColor || DEFAULTS.primaryColor);
    document.documentElement.style.setProperty("--brand-secondary", settings.secondaryColor || DEFAULTS.secondaryColor);
  }, [settings.primaryColor, settings.secondaryColor]);

  return (
    <SettingsContext.Provider value={{ settings, loaded, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) return { settings: DEFAULTS, loaded: false, refresh: () => {} };
  return ctx;
}
